import { AlertTriangle, Eye, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import AppShell from '../app/AppShell';
import {
  FeedbackMessage,
  ModalBackdrop,
  PaginationFooter,
  SaveConfirmationModal,
  ToastStack,
} from '../components/common';
import {
  PurchaseOrderEditModal,
  PurchaseOrderFilters,
  PurchaseOrderForm,
  PurchaseOrderHistoryModal,
  PurchaseOrderProgressModal,
  PurchaseOrderSupplierBalancing,
  PurchaseOrderTable,
  PurchaseOrderToolbar,
  PurchaseOrderSupplierBatch,
} from '../components/purchase-order';
import {
  createPurchaseOrderFormInitialValues,
  purchaseOrderStatuses,
  purchaseOrderStockUnits,
} from '../data/purchaseOrderConstants';
import useCancelConfirmation from '../hooks/useCancelConfirmation';
import usePurchaseOrders from '../hooks/usePurchaseOrders';
import { buildPurchaseOrderEditValues } from '../hooks/purchase-orders/purchaseOrderHelpers';
import { buildAssetUrl } from '../services/apiConfig';
import useSaveConfirmation from '../hooks/useSaveConfirmation';
import { hasChangedFormValues } from '../utils/formDirty';

const purchaseOrderTabs = new Set([
  'list',
  'create',
  'supplier-batch',
  'supplier-balancing',
  'reject-history',
]);
const addPurchaseOrderStockUnits = purchaseOrderStockUnits.filter(
  (stockUnit) => stockUnit.value === 'sacks'
);
const releasedSavingsReopenMessage =
  'Reverse this balancing savings release before reopening the saved balancing record.';

function PurchaseOrderPage({
  navigationItems,
  activePath,
  activeSearch = '',
  lockedView = '',
  onNavigate,
}) {
  const saveConfirmation = useSaveConfirmation();
  const cancelConfirmation = useCancelConfirmation();
  const [reopenBlockedMessage, setReopenBlockedMessage] = useState('');
  const [purchaseOrderActionBlock, setPurchaseOrderActionBlock] = useState(null);
  const [editBlockedBalancingRecord, setEditBlockedBalancingRecord] = useState(null);
  const [balancingPrefillSupplierId, setBalancingPrefillSupplierId] = useState('');
  const [focusBalancingRecordId, setFocusBalancingRecordId] = useState('');
  const [rejectHistoryDetails, setRejectHistoryDetails] = useState(null);
  const initialActiveView = lockedView || getPurchaseOrderTabId(activeSearch);
  const {
    activeView,
    filterValues,
    formValues,
    formErrors,
    purchaseOrders,
    allPurchaseOrders,
    deliveryDateSort,
    purchaseOrderPagination,
    purchaseOrderSearchSuggestions,
    initialSummarySubmittedPoDates,
    initialSummaryDraftPoDates,
    supplierBalancings,
    supplierOptions,
    activeSupplierOptions,
    seaweedTypeOptions,
    warehouseLocationOptions,
    lansaOptions,
    hasSuppliers,
    hasSeaweedTypes,
    hasWarehouseLocations,
    isLoading,
    isSaving,
    cancelingPurchaseOrderId,
    loadError,
    submitError,
    cancelError,
    successMessage,
    editingPurchaseOrder,
    editValues,
    editErrors,
    editError,
    isEditSaving,
    progressingPurchaseOrder,
    progressValues,
    receivingEditValues,
    initialReceivingEditValues,
    isProgressSaving,
    isReceivingSaving,
    progressError,
    historyPurchaseOrder,
    historyLogs,
    isHistoryLoading,
    historyError,
    setActiveView,
    handleFilterChange,
    handleFilterSubmit,
    handleFilterReset,
    handleStatusFilterChange,
    handleDeliveryDateSortToggle,
    handleFormChange,
    handleSubmit,
    handleSupplierBatchSubmit,
    handleSupplierBalancingSubmit,
    handleSupplierBalancingEdit,
    handleSupplierBalancingStatusChange,
    handleInitialSummaryDraftSaved,
    handleCancel,
    handleCancelPurchaseOrder,
    openEditModal,
    closeEditModal,
    handleEditChange,
    handleEditSubmit,
    openProgressModal,
    closeProgressModal,
    handleProgressChange,
    resetProgressValues,
    resetReceivingEditValues,
    handleProgressSubmit,
    handleFinalizeForBalancing,
    handleReopenReceivingForBalancing,
    handleReceivingEditChange,
    handleReceivingEditSubmit,
    openHistoryModal,
    closeHistoryModal,
  } = usePurchaseOrders(initialActiveView);
  const shouldShowReopenBlockedModal = submitError === releasedSavingsReopenMessage;
  const toasts = [
    loadError ? { id: 'po-load-error', tone: 'error', message: loadError } : null,
    submitError && !shouldShowReopenBlockedModal
      ? { id: 'po-submit-error', tone: 'error', message: submitError }
      : null,
    cancelError ? { id: 'po-cancel-error', tone: 'error', message: cancelError } : null,
    successMessage ? { id: 'po-success', tone: 'success', message: successMessage } : null,
    editError ? { id: 'po-edit-error', tone: 'error', message: editError } : null,
  ].filter(Boolean);

  useEffect(() => {
    if (lockedView) {
      setActiveView(lockedView);
      return;
    }

    setActiveView(getPurchaseOrderTabId(activeSearch));
  }, [activeSearch, lockedView, setActiveView]);

  useEffect(() => {
    setBalancingPrefillSupplierId(getSupplierBalancingPrefillSupplierId(activeSearch));
    setFocusBalancingRecordId(getSavedBalancingFocusId(activeSearch));
  }, [activeSearch]);

  useEffect(() => {
    if (shouldShowReopenBlockedModal) {
      setReopenBlockedMessage(submitError);
    }
  }, [shouldShowReopenBlockedModal, submitError]);

  function handleToolbarViewChange(viewId) {
    setBalancingPrefillSupplierId('');
    setActiveView(viewId);
    onNavigate(`${activePath}?tab=${viewId}`);
  }

  function openEditablePurchaseOrder(purchaseOrder) {
    const blockingBalancingRecord = findSupplierBalancingForPurchaseOrder(
      supplierBalancings,
      purchaseOrder?.id
    );

    if (blockingBalancingRecord) {
      setEditBlockedBalancingRecord(blockingBalancingRecord);
      return;
    }

    openEditModal(purchaseOrder);
  }

  function openSupplierBalancingForPurchaseOrder(purchaseOrder) {
    const canOpenSupplierBalancing =
      purchaseOrder?.status === 'Completed' ||
      (purchaseOrder?.status === 'Received' && Boolean(purchaseOrder.balancingReadyAt));

    if (!canOpenSupplierBalancing) {
      return;
    }

    setBalancingPrefillSupplierId(String(purchaseOrder.supplierId || ''));
    setActiveView('supplier-balancing');

    if (!lockedView) {
      onNavigate(`${activePath}?${buildSupplierBalancingSearchParams(purchaseOrder)}`);
    }
  }

  async function handleConfirmedProgressSubmit(event) {
    let updatedPurchaseOrder = null;

    if (progressingPurchaseOrder?.status === 'Received') {
      const paidBalancingRecord = findPaidSupplierBalancingForPurchaseOrder(
        supplierBalancings,
        progressingPurchaseOrder.id
      );

      if (!progressingPurchaseOrder.balancingReadyAt) {
        return false;
      }

      if (!paidBalancingRecord) {
        return false;
      }

      updatedPurchaseOrder = progressingPurchaseOrder.balancingReadyAt
        ? progressingPurchaseOrder
        : await handleReceivingEditSubmit();

      if (updatedPurchaseOrder === false) {
        return false;
      }
    }

    return handleProgressSubmit(event, updatedPurchaseOrder);
  }

  async function handleFinalizeProgressForBalancing() {
    if (progressingPurchaseOrder?.status !== 'Received') {
      return false;
    }

    const updatedPurchaseOrder = await handleReceivingEditSubmit();

    if (updatedPurchaseOrder === false) {
      return false;
    }

    const finalizedPurchaseOrder = await handleFinalizeForBalancing(updatedPurchaseOrder);

    if (finalizedPurchaseOrder === false) {
      return false;
    }

    setBalancingPrefillSupplierId(String(finalizedPurchaseOrder.supplierId || ''));
    closeProgressModal();
    setActiveView('supplier-balancing');

    if (!lockedView) {
      onNavigate(`${activePath}?${buildSupplierBalancingSearchParams(finalizedPurchaseOrder)}`);
    }

    return true;
  }

  const isCompletingPurchaseOrder = progressingPurchaseOrder?.status === 'Received';
  const progressConfirmationConfig = isCompletingPurchaseOrder
    ? {
        title: 'Confirm completed?',
        message:
          'This will save the receiving details and mark this purchase order as completed.',
        confirmLabel: 'Confirm Completed',
        confirmingLabel: 'Completing...',
        cancelLabel: 'Review',
      }
    : {
        title: 'Update purchase order progress?',
        message: 'Please confirm that you want to update this purchase order progress.',
        confirmLabel: 'Update In Progress',
        confirmingLabel: 'Updating...',
        cancelLabel: 'Review',
      };

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      {reopenBlockedMessage ? (
        <ReopenBlockedWarningModal
          message={reopenBlockedMessage}
          onClose={() => setReopenBlockedMessage('')}
        />
      ) : null}
      {purchaseOrderActionBlock ? (
        <ReopenBlockedWarningModal
          title={purchaseOrderActionBlock.title}
          message={purchaseOrderActionBlock.message}
          detail={purchaseOrderActionBlock.detail}
          onClose={() => setPurchaseOrderActionBlock(null)}
        />
      ) : null}
      {editBlockedBalancingRecord ? (
        <ReopenBlockedWarningModal
          title="Edit blocked"
          message={getPurchaseOrderEditBlockedMessage(editBlockedBalancingRecord)}
          detail="Open Supplier Balancing and edit or cancel the saved balancing record before changing this purchase order."
          onClose={() => setEditBlockedBalancingRecord(null)}
        />
      ) : null}
      <section className="mt-5 w-full min-w-0 max-w-full overflow-hidden px-3 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid w-full min-w-0 max-w-full gap-5">
          {lockedView ? null : (
            <PurchaseOrderToolbar activeView={activeView} onChangeView={handleToolbarViewChange} />
          )}

          {activeView === 'list' ? (
            <div className="grid min-w-0 gap-3">
              <div className="min-w-0">
                <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
                  Purchase Order List
                </h2>
                <p className="m-0 mt-2 max-w-full text-[0.96rem] leading-6 text-[var(--theme-text-muted)]">
                  Track supplier orders, cargo details, and delivery schedules.
                </p>
              </div>

              <PurchaseOrderFilters
                values={filterValues}
                suggestions={purchaseOrderSearchSuggestions}
                statusOptions={purchaseOrderStatuses}
                warehouseLocationOptions={warehouseLocationOptions}
                onChange={handleFilterChange}
                onStatusChange={handleStatusFilterChange}
                onSubmit={handleFilterSubmit}
                onReset={handleFilterReset}
              />

              {isLoading ? (
                <FeedbackMessage tone="info" message="Loading purchase orders..." />
              ) : (
                <div className="grid gap-3">
                  <PurchaseOrderTable
                    purchaseOrders={purchaseOrders}
                    balancingRecords={supplierBalancings}
                    warehouseLocationOptions={warehouseLocationOptions}
                    deliveryDateSort={deliveryDateSort}
                    onEditPurchaseOrder={openEditablePurchaseOrder}
                    onOpenProgress={openProgressModal}
                    onOpenSupplierBalancing={openSupplierBalancingForPurchaseOrder}
                    onCancelPurchaseOrder={(purchaseOrder) => {
                      if (purchaseOrder.hasReleaseItems) {
                        setPurchaseOrderActionBlock({
                          title: 'Cancel blocked',
                          message: `${formatPurchaseOrderCode(purchaseOrder)} is tagged for release/pre-release and can no longer be cancelled.`,
                          detail:
                            'Clear the release draft or reverse the release record before cancelling this purchase order.',
                        });
                        return;
                      }

                      saveConfirmation.requestConfirmation(null, () =>
                        handleCancelPurchaseOrder(purchaseOrder), {
                          title: 'Cancel purchase order?',
                          message: `This will cancel ${formatPurchaseOrderCode(purchaseOrder)} and disable its row actions. Continue?`,
                          confirmLabel: 'Cancel P.O.',
                          confirmingLabel: 'Cancelling...',
                          cancelLabel: 'Keep Active',
                        }
                      );
                    }}
                    cancelingPurchaseOrderId={cancelingPurchaseOrderId}
                    onToggleDeliveryDateSort={handleDeliveryDateSortToggle}
                  />
                  {purchaseOrderPagination.totalItems ? (
                    <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                      <PaginationFooter {...purchaseOrderPagination} />
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ) : activeView === 'supplier-batch' ? (
            <PurchaseOrderSupplierBatch
              supplierOptions={activeSupplierOptions}
              warehouseLocationOptions={warehouseLocationOptions}
              purchaseOrders={allPurchaseOrders}
              supplierBalancings={supplierBalancings}
              isSaving={isSaving}
              canSubmit={hasSuppliers}
              isPageLoading={isLoading}
              submittedPoDates={initialSummarySubmittedPoDates}
              draftPoDates={initialSummaryDraftPoDates}
              onSubmit={(batchValues) =>
                handleSupplierBatchSubmit(batchValues, {
                  stayOnSupplierBatch: lockedView === 'supplier-batch',
                })
              }
              onAddSupplier={() => onNavigate('/admin/supplier?view=create')}
              onRemoveGeneratedPurchaseOrder={handleCancelPurchaseOrder}
              onRequestSubmitConfirmation={(onConfirm) =>
                saveConfirmation.requestConfirmation(null, onConfirm, {
                  title: 'Submit Supplier Batch P.O.?',
                  message:
                    'This will create one purchase order for each selected supplier using the entered No. of Sacks.',
                  confirmLabel: 'Submit Batch',
                  confirmingLabel: 'Submitting...',
                  cancelLabel: 'Review',
                })
              }
              onDraftSaved={handleInitialSummaryDraftSaved}
              onRequestClearConfirmation={(onConfirm, clearSummary) =>
                saveConfirmation.requestConfirmation(null, onConfirm, {
                  title:
                    Number(clearSummary?.editableCount || 0) > 0
                      ? 'Clear selected suppliers?'
                      : 'Selected suppliers are locked',
                  message: getSupplierBatchClearMessage(clearSummary),
                  confirmLabel:
                    Number(clearSummary?.editableCount || 0) > 0 ? 'Clear Editable' : 'Got it',
                  confirmingLabel:
                    Number(clearSummary?.editableCount || 0) > 0 ? 'Clearing...' : 'Checking...',
                  cancelLabel:
                    Number(clearSummary?.editableCount || 0) > 0 ? 'Keep Suppliers' : 'Close',
                })
              }
              onRequestRemoveGeneratedPurchaseOrderConfirmation={(purchaseOrder, onConfirm) =>
                saveConfirmation.requestConfirmation(null, onConfirm, {
                  title: 'Remove generated P.O.?',
                  message: `This will remove ${formatPurchaseOrderCode(
                    purchaseOrder
                  )} from the active Purchase Order List and remove this supplier from the reopened Initial Summary P.O.`,
                  confirmLabel: 'Remove',
                  confirmingLabel: 'Removing...',
                  cancelLabel: 'Keep Supplier',
                })
              }
              onRequestReopenConfirmation={(onConfirm) =>
                saveConfirmation.requestConfirmation(null, onConfirm, {
                  title: 'Reopen submitted Initial Summary P.O.?',
                  message:
                    'This will unlock the selected suppliers for editing. Existing purchase orders already used in saved supplier balancing cannot be removed.',
                  confirmLabel: 'Reopen',
                  confirmingLabel: 'Reopening...',
                  cancelLabel: 'Keep Submitted',
                })
              }
            />
          ) : activeView === 'supplier-balancing' ? (
            <PurchaseOrderSupplierBalancing
              purchaseOrders={allPurchaseOrders}
              supplierOptions={supplierOptions}
              balancingRecords={supplierBalancings}
              prefillSupplierId={balancingPrefillSupplierId}
              focusBalancingRecordId={focusBalancingRecordId}
              shouldFilterFocusedBalancingRecord={Boolean(balancingPrefillSupplierId)}
              showHeaderIcon={Boolean(lockedView)}
              isSaving={isSaving}
              onSave={handleSupplierBalancingSubmit}
              onEdit={handleSupplierBalancingEdit}
              onRequestSaveConfirmation={(onConfirm) =>
                saveConfirmation.requestConfirmation(null, onConfirm, {
                  title: 'Save supplier balancing?',
                  message:
                    'Please confirm the selected P.O. sacks, weights, additions, and deductions before saving this balancing record.',
                  confirmLabel: 'Save Balancing',
                  confirmingLabel: 'Saving...',
                  cancelLabel: 'Review',
                })
              }
              onRequestStatusConfirmation={(record, nextStatus, onConfirm) =>
                saveConfirmation.requestConfirmation(null, onConfirm, {
                  title:
                    nextStatus === 'Paid'
                      ? 'Mark balancing as paid?'
                      : nextStatus === 'Cancelled'
                        ? 'Cancel supplier balancing?'
                        : 'Reopen balancing payment?',
                  message:
                    nextStatus === 'Paid'
                      ? `This will mark ${getBalancingSupplierLabel(record)} balancing as paid.`
                      : nextStatus === 'Cancelled'
                        ? `This will cancel ${getBalancingSupplierLabel(record)} balancing and make its P.O.s available again.`
                        : `This will move ${getBalancingSupplierLabel(record)} balancing back to For Payment.`,
                  confirmLabel:
                    nextStatus === 'Paid'
                      ? 'Mark Paid'
                      : nextStatus === 'Cancelled'
                        ? 'Cancel Balancing'
                        : 'Reopen',
                  confirmingLabel: 'Updating...',
                  cancelLabel: 'Review',
                })
              }
              onRequestEditConfirmation={(record, onConfirm) =>
                saveConfirmation.requestConfirmation(null, onConfirm, {
                  title: 'Edit supplier balancing?',
                  message: `This will remove ${getBalancingSupplierLabel(record)} from saved records and load its values back into the balancing form.`,
                  confirmLabel: 'Edit Balancing',
                  confirmingLabel: 'Loading...',
                  cancelLabel: 'Keep Saved',
                })
              }
              onStatusChange={handleSupplierBalancingStatusChange}
            />
          ) : activeView === 'reject-history' ? (
            <RejectHistoryView
              purchaseOrders={allPurchaseOrders}
              balancingRecords={supplierBalancings}
              onViewDetails={setRejectHistoryDetails}
            />
          ) : (
            <>
              {!isLoading && !hasSuppliers ? (
                <FeedbackMessage
                  tone="warning"
                  message="Add or activate at least one supplier first so you can create a purchase order."
                  action={{
                    label: 'Add Supplier',
                    onClick: () => onNavigate('/admin/supplier?view=create'),
                  }}
                />
              ) : null}
              {!isLoading && !hasSeaweedTypes ? (
                <FeedbackMessage
                  tone="warning"
                  message="Add at least one seaweed type first so you can create a purchase order."
                  action={{
                    label: 'Add Seaweed Type',
                    onClick: () => onNavigate('/admin/lansa?tab=seaweed-type'),
                  }}
                />
              ) : null}
              {!isLoading && !hasWarehouseLocations ? (
                <FeedbackMessage
                  tone="warning"
                  message="Add at least one warehouse location first so you can create a purchase order."
                  action={{
                    label: 'Add Warehouse Location',
                    onClick: () => onNavigate('/admin/lansa?tab=warehouse-location'),
                  }}
                />
              ) : null}
              <PurchaseOrderForm
                values={formValues}
                errors={formErrors}
                supplierOptions={activeSupplierOptions}
                seaweedTypeOptions={seaweedTypeOptions}
                warehouseLocationOptions={warehouseLocationOptions}
                stockUnits={addPurchaseOrderStockUnits}
                statusOptions={purchaseOrderStatuses}
                isSaving={isSaving}
                canCreatePurchaseOrder={
                  hasSuppliers && hasSeaweedTypes && hasWarehouseLocations
                }
                onChange={handleFormChange}
                onSubmit={(event) =>
                  saveConfirmation.requestConfirmation(event, handleSubmit, {
                    title: 'Save purchase order?',
                    message: 'Please confirm the purchase order details before saving.',
                    confirmLabel: 'Save P.O.',
                  })
                }
                onCancel={(event) =>
                  cancelConfirmation.requestCancel(event, {
                    shouldConfirm: () =>
                      hasChangedFormValues(formValues, createPurchaseOrderFormInitialValues()),
                    onCancel: handleCancel,
                    title: 'Cancel purchase order?',
                    message: 'You have unsaved purchase order inputs. Discard them?',
                  })
                }
              />
            </>
          )}
        </div>
      </section>

      {editingPurchaseOrder ? (
        <PurchaseOrderEditModal
          values={editValues}
          errors={editErrors}
          supplierOptions={supplierOptions}
          lansaOptions={lansaOptions}
          seaweedTypeOptions={seaweedTypeOptions}
          warehouseLocationOptions={warehouseLocationOptions}
          stockUnits={purchaseOrderStockUnits}
          currentStatus={editingPurchaseOrder.status}
          isSaving={isEditSaving}
          onChange={handleEditChange}
          onOpenHistory={() => {
            closeEditModal();
            openHistoryModal(editingPurchaseOrder);
          }}
          onClose={(event) =>
            cancelConfirmation.requestCancel(event, {
              shouldConfirm: () =>
                editingPurchaseOrder
                  ? hasChangedFormValues(
                      editValues,
                      buildPurchaseOrderEditValues(editingPurchaseOrder)
                    )
                  : false,
              onCancel: closeEditModal,
              title: 'Cancel editing?',
              message: 'You have unsaved purchase order changes. Discard them?',
            })
          }
          onSubmit={(event) =>
            saveConfirmation.requestConfirmation(event, handleEditSubmit, {
              title: 'Save purchase order changes?',
              message: 'Please confirm the updated purchase order details before saving.',
              confirmLabel: 'Save Changes',
            })
          }
        />
      ) : null}

      {progressingPurchaseOrder ? (
        <PurchaseOrderProgressModal
          purchaseOrder={progressingPurchaseOrder}
          values={progressValues}
          lansaOptions={lansaOptions}
          warehouseLocationOptions={warehouseLocationOptions}
          receivingEditValues={receivingEditValues}
          initialReceivingEditValues={initialReceivingEditValues}
          isSaving={isProgressSaving}
          isReceivingSaving={isReceivingSaving}
          error={progressError}
          hasPaidBalancing={Boolean(
            findPaidSupplierBalancingForPurchaseOrder(
              supplierBalancings,
              progressingPurchaseOrder.id
            )
          )}
          hasSavedBalancing={Boolean(
            findSavedSupplierBalancingForPurchaseOrder(
              supplierBalancings,
              progressingPurchaseOrder.id
            )
          )}
          onChange={handleProgressChange}
          onReceivingChange={handleReceivingEditChange}
          onClose={closeProgressModal}
          onResetProgress={resetProgressValues}
          onResetReceiving={resetReceivingEditValues}
          onFinalizeForBalancing={(event) =>
            saveConfirmation.requestConfirmation(event, handleFinalizeProgressForBalancing, {
              title: 'Finalize receiving for balancing?',
              message:
                'This will save the receiving details, lock the Step 3 fields, and open Supplier Balancing. Confirm Completed will stay blocked until this P.O. balancing is marked Paid.',
              confirmLabel: 'Finalize',
              confirmingLabel: 'Finalizing...',
              cancelLabel: 'Review',
            })
          }
          onReopenReceiving={(event) =>
            saveConfirmation.requestConfirmation(event, handleReopenReceivingForBalancing, {
              title: 'Reopen receiving details?',
              message:
                'This will unlock Step 3 so you can edit the receiving fields before saving it for Supplier Balancing again.',
              confirmLabel: 'Reopen Step 3',
              confirmingLabel: 'Reopening...',
              cancelLabel: 'Keep Finalized',
            })
          }
          onSubmit={(event) =>
            saveConfirmation.requestConfirmation(
              event,
              handleConfirmedProgressSubmit,
              progressConfirmationConfig
            )
          }
        />
      ) : null}

      {historyPurchaseOrder ? (
        <PurchaseOrderHistoryModal
          purchaseOrder={historyPurchaseOrder}
          historyLogs={historyLogs}
          isLoading={isHistoryLoading}
          error={historyError}
          onBack={() => {
            closeHistoryModal();
            openEditablePurchaseOrder(historyPurchaseOrder);
          }}
          onClose={closeHistoryModal}
        />
      ) : null}

      {rejectHistoryDetails ? (
        <RejectHistoryDetailsModal
          row={rejectHistoryDetails}
          onClose={() => setRejectHistoryDetails(null)}
        />
      ) : null}

      <SaveConfirmationModal {...saveConfirmation.modalProps} />
      <SaveConfirmationModal {...cancelConfirmation.modalProps} />
    </AppShell>
  );
}

function RejectHistoryView({ purchaseOrders = [], balancingRecords = [], onViewDetails }) {
  const rows = buildRejectHistoryRows(purchaseOrders, balancingRecords);

  return (
    <section className="grid min-w-0 gap-3">
      <div className="min-w-0">
        <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
          Reject History
        </h2>
        <p className="m-0 mt-2 max-w-3xl text-[0.96rem] leading-6 text-[var(--theme-text-muted)]">
          Read-only reject records kept for supplier balancing and capital audit history.
        </p>
      </div>

      <div className="relative overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[1080px] border-collapse">
            <thead>
              <tr className="bg-[var(--theme-accent-panel)]">
                <RejectHistoryHeading>P.O. Code</RejectHistoryHeading>
                <RejectHistoryHeading>Supplier / Sacks Mark</RejectHistoryHeading>
                <RejectHistoryHeading>Received Date</RejectHistoryHeading>
                <RejectHistoryHeading>Reject Net KG</RejectHistoryHeading>
                <RejectHistoryHeading>Reject Adjusted KG</RejectHistoryHeading>
                <RejectHistoryHeading>Price / Kilo</RejectHistoryHeading>
                <RejectHistoryHeading>Subtotal / Capital Value</RejectHistoryHeading>
                <RejectHistoryHeading>Balancing Status</RejectHistoryHeading>
                <RejectHistoryHeading>Details</RejectHistoryHeading>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((row) => {
                  const isUnbalanced = isRejectHistoryUnbalanced(row.balancingStatus);

                  return (
                    <tr
                      key={row.id}
                      className="border-t border-[var(--theme-border-soft)] text-[0.84rem] text-[var(--theme-text)] transition-colors hover:bg-[var(--theme-surface-soft)]"
                    >
                    <RejectHistoryCell>
                      <span className="font-semibold">{row.poCode}</span>
                    </RejectHistoryCell>
                    <RejectHistoryCell>
                      <span className="block max-w-[190px] truncate" title={row.supplierLabel}>
                        {row.supplierLabel}
                      </span>
                    </RejectHistoryCell>
                    <RejectHistoryCell>{formatDisplayDate(row.receivedAt)}</RejectHistoryCell>
                    <RejectHistoryCell>{formatKg(row.rejectNetKg)}</RejectHistoryCell>
                    <RejectHistoryCell>
                      {isUnbalanced ? (
                        <span className="text-[var(--theme-text-muted)]">-</span>
                      ) : (
                        formatKg(row.rejectAdjustedKg)
                      )}
                    </RejectHistoryCell>
                    <RejectHistoryCell>{formatCurrency(row.pricePerKilo)}</RejectHistoryCell>
                    <RejectHistoryCell>
                      {isUnbalanced ? (
                        <span className="text-[var(--theme-text-muted)]">-</span>
                      ) : (
                        <strong>{formatCurrency(row.subtotal)}</strong>
                      )}
                    </RejectHistoryCell>
                    <RejectHistoryCell>
                      <RejectHistoryStatusBadge status={row.balancingStatus} />
                    </RejectHistoryCell>
                    <RejectHistoryCell>
                      <button
                        className="inline-flex min-h-[34px] items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]"
                        type="button"
                        onClick={() => onViewDetails(row)}
                      >
                        <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                        View details
                      </button>
                    </RejectHistoryCell>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    className="px-4 py-12 text-center text-[0.9rem] text-[var(--theme-text-muted)]"
                    colSpan={9}
                  >
                    No reject history found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 p-4 md:hidden">
          {rows.length ? (
            rows.map((row) => {
              const isUnbalanced = isRejectHistoryUnbalanced(row.balancingStatus);

              return (
                <article
                  key={row.id}
                  className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3"
                >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <strong className="block text-[1rem] text-[var(--theme-text)]">{row.poCode}</strong>
                    <span className="mt-1 block truncate text-[0.9rem] text-[var(--theme-text-muted)]">
                      {row.supplierLabel}
                    </span>
                  </div>
                  <RejectHistoryStatusBadge status={row.balancingStatus} />
                </div>
                <div className="mt-3 grid gap-1.5 text-[0.86rem] text-[var(--theme-text-subtle)]">
                  <RejectHistoryMeta label="Received Date" value={formatDisplayDate(row.receivedAt)} />
                  <RejectHistoryMeta label="Reject Net KG" value={formatKg(row.rejectNetKg)} />
                  {!isUnbalanced ? (
                    <RejectHistoryMeta label="Reject Adjusted KG" value={formatKg(row.rejectAdjustedKg)} />
                  ) : null}
                  <RejectHistoryMeta label="Price / Kilo" value={formatCurrency(row.pricePerKilo)} />
                  {!isUnbalanced ? (
                    <RejectHistoryMeta label="Subtotal" value={formatCurrency(row.subtotal)} />
                  ) : null}
                </div>
                <button
                  className="mt-3 inline-flex min-h-[36px] w-full items-center justify-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)]"
                  type="button"
                  onClick={() => onViewDetails(row)}
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                  View details
                </button>
                </article>
              );
            })
          ) : (
            <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12 text-center text-[0.9rem] text-[var(--theme-text-muted)]">
              No reject history found.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function RejectHistoryDetailsModal({ row, onClose }) {
  const receiptParts = splitRejectReceiptDetails(row.rejectItem?.remarks);
  const imagePath = row.rejectItem?.image || '';
  const isUnbalanced = isRejectHistoryUnbalanced(row.balancingStatus);

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.55)]"
      contentClassName="!px-4"
      zIndexClassName="z-[70]"
    >
      <div className="mx-auto w-full max-w-3xl rounded-[22px] border border-[var(--theme-border)] bg-white p-4 shadow-[0_24px_70px_rgba(var(--theme-shadow-rgb),0.32)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <strong className="text-[1rem] text-[var(--theme-text)]">
              {row.poCode} Reject Details
            </strong>
            <p className="m-0 mt-1 text-[0.86rem] text-[var(--theme-text-muted)]">
              {row.supplierLabel} / {formatDisplayDate(row.receivedAt)}
            </p>
          </div>
          <button
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            aria-label="Close reject details"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <RejectHistoryTile label="Reject Net KG" value={formatKg(row.rejectNetKg)} />
          {!isUnbalanced ? (
            <RejectHistoryTile label="Reject Adjusted KG" value={formatKg(row.rejectAdjustedKg)} />
          ) : null}
          {!isUnbalanced ? (
            <RejectHistoryTile label="Subtotal" value={formatCurrency(row.subtotal)} />
          ) : null}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
            <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
              Receipt Details
            </strong>
            <p className="m-0 mt-2 whitespace-pre-wrap text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
              {receiptParts.receiptDetails || 'No captured receipt details found.'}
            </p>
            {imagePath ? (
              <a
                className="mt-3 inline-flex text-[0.78rem] font-semibold text-[var(--theme-accent)] underline-offset-2 hover:text-[var(--theme-accent-strong)] hover:underline"
                href={buildAssetUrl(imagePath)}
                target="_blank"
                rel="noreferrer"
              >
                Open receipt image
              </a>
            ) : null}
          </div>
          <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
            <strong className="block text-[0.78rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
              Remarks
            </strong>
            <p className="m-0 mt-2 whitespace-pre-wrap text-[0.84rem] leading-5 text-[var(--theme-text-subtle)]">
              {receiptParts.remarks || '-'}
            </p>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function RejectHistoryHeading({ children }) {
  return (
    <th className="whitespace-nowrap px-3 py-2.5 text-left text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function RejectHistoryCell({ children }) {
  return (
    <td className="whitespace-nowrap px-3 py-2.5 align-middle lg:px-4">
      {children}
    </td>
  );
}

function RejectHistoryMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <span className="text-right font-semibold text-[var(--theme-text)]">{value}</span>
    </div>
  );
}

function RejectHistoryTile({ label, value }) {
  return (
    <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-3">
      <span className="block text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-1 block text-[0.98rem] text-[var(--theme-text-strong)]">
        {value}
      </strong>
    </div>
  );
}

function RejectHistoryStatusBadge({ status }) {
  const normalizedStatus = String(status || 'Unbalanced').trim();
  const statusClassName =
    normalizedStatus === 'Paid'
      ? 'border-[#b8dec0] bg-[#effaf1] text-[#2f7a3b]'
      : normalizedStatus === 'Cancelled'
        ? 'border-[#efc3b8] bg-[#fff6f3] text-[#a34334]'
        : normalizedStatus === 'For Payment'
          ? 'border-[#ead7a8] bg-[#fff8e6] text-[#8a6424]'
          : 'border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] text-[var(--theme-text-subtle)]';

  return (
    <span className={`inline-flex min-h-[28px] items-center rounded-full border px-3 text-[0.72rem] font-semibold ${statusClassName}`}>
      {normalizedStatus}
    </span>
  );
}

function isRejectHistoryUnbalanced(status) {
  return String(status || 'Unbalanced').trim().toLowerCase() === 'unbalanced';
}

function buildRejectHistoryRows(purchaseOrders = [], balancingRecords = []) {
  const purchaseOrdersById = new Map(
    purchaseOrders.map((purchaseOrder) => [String(purchaseOrder.id), purchaseOrder])
  );
  const rejectTotalsByBalancingId = new Map();

  return purchaseOrders
    .flatMap((purchaseOrder) => {
      const rejectItems = getRejectReceivingItems(purchaseOrder);
      const balancingRecord = findSupplierBalancingForPurchaseOrder(
        balancingRecords,
        purchaseOrder.id
      );

      return rejectItems.map((rejectItem, index) => {
        const rejectNetKg = getNumericValue(rejectItem.quantityKg || rejectItem.rawNetKg);
        const itemRejectKg = rejectNetKg;
        const balancingTotalRejectKg = balancingRecord
          ? getBalancingRejectSacksTotal(
              balancingRecord,
              purchaseOrdersById,
              rejectTotalsByBalancingId
            )
          : 0;
        const rejectShare =
          balancingTotalRejectKg > 0 && rejectNetKg > 0 ? rejectNetKg / balancingTotalRejectKg : 0;
        const balancingRejectKg = getNumericValue(balancingRecord?.totals?.rejectAdjustedKilo);
        const balancingRejectPay = getNumericValue(balancingRecord?.totals?.rejectPay);
        const rejectAdjustedKg =
          balancingRecord && balancingRejectKg > 0 && rejectShare > 0
            ? balancingRejectKg * rejectShare
            : itemRejectKg;
        const pricePerKilo = getRejectPricePerKilo(purchaseOrder, rejectItem, balancingRecord);
        const subtotal =
          balancingRecord && balancingRejectPay > 0 && rejectShare > 0
            ? balancingRejectPay * rejectShare
            : rejectAdjustedKg * pricePerKilo;

        return {
          id: `${purchaseOrder.id}-${rejectItem.id || index}`,
          purchaseOrderId: purchaseOrder.id,
          poCode: formatPurchaseOrderCode(purchaseOrder),
          supplierLabel: formatSupplierSacksMarkLabel(purchaseOrder),
          receivedAt: rejectItem.receivedAt || purchaseOrder.receivedAt || purchaseOrder.updatedAt,
          rejectSacks: rejectNetKg,
          rejectNetKg,
          rejectAdjustedKg,
          moistureContent: rejectItem.moistureContent,
          pricePerKilo,
          subtotal,
          balancingStatus: balancingRecord?.status || 'Unbalanced',
          rejectItem,
          balancingRecord,
        };
      });
    })
    .filter((row) => row.rejectNetKg > 0 || row.rejectAdjustedKg > 0)
    .sort((first, second) => {
      const firstDate = new Date(first.receivedAt || 0).getTime();
      const secondDate = new Date(second.receivedAt || 0).getTime();

      return secondDate - firstDate;
    });
}

function getRejectReceivingItems(purchaseOrder) {
  return (Array.isArray(purchaseOrder?.receivingItems) ? purchaseOrder.receivingItems : []).filter(
    (item) => String(item?.category || '').toLowerCase() === 'reject'
  );
}

function getBalancingRejectSacksTotal(record, purchaseOrdersById, cache) {
  const cacheKey = String(record?.id || '');

  if (cacheKey && cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const total = (record?.purchaseOrderIds || []).reduce((sum, purchaseOrderId) => {
    const purchaseOrder = purchaseOrdersById.get(String(purchaseOrderId));
    const purchaseOrderRejectSacks = getRejectReceivingItems(purchaseOrder).reduce(
      (rejectSum, item) => rejectSum + getNumericValue(item.quantityKg || item.rawNetKg),
      0
    );

    return sum + purchaseOrderRejectSacks;
  }, 0);

  if (cacheKey) {
    cache.set(cacheKey, total);
  }

  return total;
}

function getRejectPricePerKilo(purchaseOrder, rejectItem, balancingRecord) {
  return firstPositiveNumber([
    balancingRecord?.inputs?.pricePerKilo,
    rejectItem?.unitCost,
    purchaseOrder?.actualPricePerKilo,
    purchaseOrder?.actualPrice,
    purchaseOrder?.pricePerKilo,
    purchaseOrder?.initialPricePerKilo,
    purchaseOrder?.price,
  ]);
}

function firstPositiveNumber(values = []) {
  for (const value of values) {
    const numberValue = getNumericValue(value);

    if (numberValue > 0) {
      return numberValue;
    }
  }

  return 0;
}

function getNumericValue(value) {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const numberValue = Number(String(value).replace(/,/g, ''));

  return Number.isFinite(numberValue) ? numberValue : 0;
}

function formatSupplierSacksMarkLabel(purchaseOrder) {
  const sacksMark = purchaseOrder?.sacksMark || purchaseOrder?.supplier?.sacksMark;
  const supplierName =
    purchaseOrder?.supplierName || purchaseOrder?.supplier?.companyName || purchaseOrder?.supplier?.name;

  return [supplierName, sacksMark].filter(Boolean).join(' / ') || '-';
}

function formatDisplayDate(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatQuantity(value, unit = '') {
  const numberValue = getNumericValue(value);
  const formattedValue = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(numberValue);

  return unit ? `${formattedValue} ${unit}` : formattedValue;
}

function formatKg(value) {
  return `${formatQuantity(value)} kg`;
}

function formatCurrency(value) {
  const numberValue = getNumericValue(value);

  return `₱${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberValue)}`;
}

function splitRejectReceiptDetails(value = '') {
  const text = String(value || '').trim();

  if (!text) {
    return { receiptDetails: '', remarks: '' };
  }

  const marker = 'Receipt Details:';
  const markerIndex = text.indexOf(marker);

  if (markerIndex === -1) {
    return { receiptDetails: '', remarks: text };
  }

  return {
    remarks: text.slice(0, markerIndex).trim(),
    receiptDetails: text.slice(markerIndex + marker.length).trim(),
  };
}

function formatPurchaseOrderCode(purchaseOrder) {
  return purchaseOrder.poCode || `PO-${String(purchaseOrder.id).padStart(4, '0')}`;
}

function getBalancingSupplierLabel(record) {
  return [record?.sacksMark, record?.supplierName].filter(Boolean).join(' - ') || 'this supplier';
}

function findSupplierBalancingForPurchaseOrder(balancingRecords = [], purchaseOrderId) {
  const normalizedPurchaseOrderId = String(purchaseOrderId || '');

  if (!normalizedPurchaseOrderId) {
    return null;
  }

  return balancingRecords.find((record) =>
    (record.purchaseOrderIds || []).some(
      (recordPurchaseOrderId) => String(recordPurchaseOrderId) === normalizedPurchaseOrderId
    )
  ) || null;
}

function findPaidSupplierBalancingForPurchaseOrder(balancingRecords = [], purchaseOrderId) {
  const normalizedPurchaseOrderId = String(purchaseOrderId || '');

  if (!normalizedPurchaseOrderId) {
    return null;
  }

  return balancingRecords.find(
    (record) =>
      record.status === 'Paid' &&
      (record.purchaseOrderIds || []).some(
        (recordPurchaseOrderId) => String(recordPurchaseOrderId) === normalizedPurchaseOrderId
      )
  ) || null;
}

function findSavedSupplierBalancingForPurchaseOrder(balancingRecords = [], purchaseOrderId) {
  const normalizedPurchaseOrderId = String(purchaseOrderId || '');

  if (!normalizedPurchaseOrderId) {
    return null;
  }

  return balancingRecords.find(
    (record) =>
      ['For Payment', 'Paid'].includes(record.status) &&
      (record.purchaseOrderIds || []).some(
        (recordPurchaseOrderId) => String(recordPurchaseOrderId) === normalizedPurchaseOrderId
      )
  ) || null;
}

function getPurchaseOrderEditBlockedMessage(record) {
  const status = String(record?.status || '').trim();
  const reference = record?.id
    ? `Reference ${formatSupplierBalancingReference(record.id)}`
    : 'A saved supplier balancing record';
  const statusLabel = status ? ` with ${status} status` : '';

  return `${reference}${statusLabel} already includes this purchase order.`;
}

function formatSupplierBalancingReference(id) {
  return `SB-${String(id).padStart(4, '0')}`;
}

function getSupplierBatchClearMessage(clearSummary = {}) {
  const editableCount = Number(clearSummary.editableCount || 0);
  const lockedCount = Number(clearSummary.lockedCount || 0);
  const statusText = Array.isArray(clearSummary.lockedStatuses) && clearSummary.lockedStatuses.length
    ? ` (${clearSummary.lockedStatuses.join(', ')})`
    : '';

  if (lockedCount > 0 && editableCount > 0) {
    return `${formatCount(editableCount, 'editable supplier')} will be cleared and its Initial generated P.O. will be removed from the active Purchase Order List. ${formatCount(lockedCount, 'locked supplier')} will stay unchanged${statusText}.`;
  }

  if (lockedCount > 0) {
    return `No selected suppliers can be cleared because ${formatCount(lockedCount, 'supplier')} already has a locked generated P.O.${statusText}. They will stay unchanged.`;
  }

  return `This will clear ${formatCount(editableCount, 'selected supplier')} and remove their Initial generated P.O.s from the active Purchase Order List when available.`;
}

function formatCount(count, singularLabel) {
  const normalizedCount = Number(count || 0);

  return `${normalizedCount} ${singularLabel}${normalizedCount === 1 ? '' : 's'}`;
}

function ReopenBlockedWarningModal({
  title = 'Reopen blocked',
  message,
  detail = 'Open Supplier Savings Details and reverse the released savings for this balancing record first.',
  onClose,
}) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.22)] backdrop-blur-[3px]"
      contentClassName="items-center"
      zIndexClassName="z-[70]"
    >
      <div className="w-full max-w-md rounded-[22px] border border-[#f1d5a9] bg-white p-6 shadow-[0_24px_60px_rgba(var(--theme-shadow-rgb),0.2)]">
        <div className="flex items-start gap-3">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-[14px] bg-[#fff3db] text-[#b57424]">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h3 className="m-0 text-[1.12rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
              {title}
            </h3>
            <p className="m-0 mt-2 text-[0.94rem] leading-6 text-[var(--theme-text-muted)]">
              {message}
            </p>
            <p className="m-0 mt-2 text-[0.88rem] leading-5 text-[var(--theme-text-muted)]">
              {detail}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            className="inline-flex min-h-[42px] items-center justify-center rounded-[14px] bg-[var(--theme-text-subtle)] px-5 text-[0.92rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.18)] outline-none transition hover:bg-[var(--theme-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] focus-visible:ring-offset-2"
            type="button"
            onClick={onClose}
          >
            Got it
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function getPurchaseOrderTabId(search) {
  const searchParams = new URLSearchParams(search);
  const requestedTabId = searchParams.get('tab');

  if (purchaseOrderTabs.has(requestedTabId)) {
    return requestedTabId;
  }

  if (searchParams.get('view') === 'create') {
    return 'create';
  }

  return 'list';
}

function getSupplierBalancingPrefillSupplierId(search) {
  const searchParams = new URLSearchParams(search);
  return String(searchParams.get('supplierId') || '').trim();
}

function getSavedBalancingFocusId(search) {
  const searchParams = new URLSearchParams(search);
  return String(searchParams.get('savedBalancingId') || '').trim();
}

function buildSupplierBalancingSearchParams(purchaseOrder) {
  const searchParams = new URLSearchParams({ tab: 'supplier-balancing' });
  const supplierId = String(purchaseOrder?.supplierId || '').trim();

  if (supplierId) {
    searchParams.set('supplierId', supplierId);
  }

  return searchParams.toString();
}

export default PurchaseOrderPage;
